import { useFeatureGate } from '@statsig/react-bindings';
import { minutesToMilliseconds } from 'date-fns';
import { useAtomValue } from 'jotai';
import { useCallback, useEffect, useState } from 'react';
import { useInterval } from 'usehooks-ts';

import { useAtomCallbackMemoized } from '../../hooks/useAtomCallbackMemoized';
import { appointmentInfoAtom } from '../../state';
import { providerWaitingRoomTranscriptionConsentModalGate } from '../../statsig/gates';
import { roomAtom } from '../../video-sdk';
import { ConsentStatus } from '../../video-sdk/core/message/constants';
import { providerShortIdAtom } from '../state';
import { useAppointmentWithinConsentWindowCallback } from './useAppointmentWithinConsentWindowCallback';
import { useCanProviderUseTranscription } from './useCanProviderUseTranscriptionCallback';
import { useGetProviderConsent } from './useGetProviderPatientConsent';
import { recordModalDismissalForProvider, shouldShowModalForProvider } from './useProviderConsentModalDismissal';

/**
 * Dependencies for useProviderTranscriptionConsentModal that can be overridden
 * for testing.
 */
export type UseProviderTranscriptionConsentModalDeps = {
  canUseTranscription: boolean;
  getHasAppointmentInWindow: () => boolean;
  getIsInSession: () => boolean;
  getShouldShowModal: () => boolean;
  nextAppointment: { timeStart: string } | null;
  providerShortId?: string;
  recordDismissal: () => void;
  useFeatureGate: typeof useFeatureGate;
  useGetProviderConsent: typeof useGetProviderConsent;
};

/**
 * Resolves dependencies for useProviderTranscriptionConsentModal with overrides
 * for testing.
 */
export function useDefaultProviderTranscriptionConsentModalDependencies(
  overrides: Partial<UseProviderTranscriptionConsentModalDeps> = {},
): Omit<UseProviderTranscriptionConsentModalDeps, 'nextAppointment'> {
  const defaultProviderShortId = useAtomValue(providerShortIdAtom);
  const defaultGetIsInSession = useAtomCallbackMemoized((get) => !!get(roomAtom), []);
  const defaultCanUseTranscription = useCanProviderUseTranscription();
  const nextAppointment = useAtomValue(appointmentInfoAtom);
  const defaultGetHasAppointmentInWindow = useAppointmentWithinConsentWindowCallback(
    overrides.nextAppointment !== undefined ? overrides.nextAppointment : nextAppointment,
  );

  return {
    canUseTranscription: defaultCanUseTranscription,
    getHasAppointmentInWindow: defaultGetHasAppointmentInWindow,
    getShouldShowModal: shouldShowModalForProvider,
    getIsInSession: defaultGetIsInSession,
    providerShortId: defaultProviderShortId,
    recordDismissal: recordModalDismissalForProvider,
    useFeatureGate,
    useGetProviderConsent,
    ...overrides,
  };
}

export const RECHECK_INTERVAL_MS = minutesToMilliseconds(1);

/**
 * Hook that orchestrates the provider transcription consent modal display logic.
 *
 * The modal is shown when all of the following conditions are met:
 * 1. Feature gate is enabled
 * 2. Provider is not in an active session
 * 3. Provider has not yet consented or declined (consent status is UNDECIDED)
 * 4. Provider's next appointment is starting in ≥3 minutes and ≤5 minutes
 * 5. Provider has not dismissed the modal today (tracked in localStorage)
 * 6. Provider can use transcription (not a prescriber without the feature gate)
 *
 * @param useDependencies - Hook that provides dependencies, can be overridden for testing
 * @returns Modal state and handlers
 */
export function useProviderTranscriptionConsentModal(
  useDependencies = useDefaultProviderTranscriptionConsentModalDependencies,
) {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const {
    canUseTranscription,
    getHasAppointmentInWindow,
    getIsInSession,
    getShouldShowModal,
    providerShortId,
    recordDismissal,
    useFeatureGate,
    useGetProviderConsent,
  } = useDependencies();

  const { value: isFeatureEnabled } = useFeatureGate(providerWaitingRoomTranscriptionConsentModalGate);

  const { providerConsent, loading } = useGetProviderConsent(providerShortId);

  const openModalWhenConditionsAreMet = useCallback(() => {
    const shouldShow =
      isFeatureEnabled &&
      !loading &&
      !getIsInSession() &&
      canUseTranscription &&
      getShouldShowModal() &&
      providerConsent === ConsentStatus.UNDECIDED &&
      getHasAppointmentInWindow();

    if (shouldShow) {
      setIsModalOpen(true);
    }
  }, [
    canUseTranscription,
    getHasAppointmentInWindow,
    getIsInSession,
    getShouldShowModal,
    isFeatureEnabled,
    loading,
    providerConsent,
  ]);

  // Run the check immediately to see if we should open the modal.
  useEffect(
    function checkAndOpenConsentModal() {
      openModalWhenConditionsAreMet();
      // If a dependency changes, we are safe to re-run the check immediately.
    },
    [openModalWhenConditionsAreMet],
  );

  // Re-check every 1 minute. Only sets up interval if feature is enabled.
  useInterval(openModalWhenConditionsAreMet, isFeatureEnabled ? RECHECK_INTERVAL_MS : null);

  const onClose = useCallback(() => setIsModalOpen(false), []);

  return {
    isModalOpen,
    onClose,
    onDismiss: recordDismissal,
    providerShortId,
  };
}
